import { useGetEnrollmentHYCSSecureAccountData, useAuthsvsPasskeys } from '@egw/api'
import { SvgPasskeyAdded } from '@egw/assets'
import { registerPasskey } from '@egw/auth'
import { Button, GenericError, IconPasskey, Link } from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { ArrowRightIcon } from '@heroicons/react/20/solid'
import { useEffect, useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { type Config } from '../../../config/config'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
// import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Multi - CreatePasskey')

export const CreatePasskeyPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const config = configClient.getConfig() as Config

  // useValidateRoute()

  const [creatingPasskey, setCreatingPasskey] = useState<boolean>(false)
  const [passkeyPersisted, setPasskeyPersisted] = useState<boolean>(false)
  const [apiError, setApiError] = useState<boolean>(false)

  const { data, isLoading, error } = useGetEnrollmentHYCSSecureAccountData()
  const {
    data: allPasskeys,
    isLoading: isLoadingPasskeys,
    isError: isErrorPasskeys,
  } = useAuthsvsPasskeys({
    enabled: true,
    refetchOnMount: 'always',
  })

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : null

  useEffect(() => {
    if (allPasskeys && allPasskeys.length > 0) {
      setPasskeyPersisted(true)
    }
  }, [allPasskeys])

  // Prevent going back in history
  useEffect(() => {
    // Push a new state to the history stack
    window.history.pushState(null, '', window.location.href)

    // Add event listener for popstate
    const handlePopState = () => {
      // Push another state to prevent going back
      window.history.pushState(null, '', window.location.href)
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  useEffect(() => {
    if (data?.__typename === 'SubmitEnrollmentSuccess' && data.enrollmentData?.passkeyRegistered) {
      setPasskeyPersisted(true)
    }
  }, [data])

  useEffect(() => {
    sendEvent('bankmulti_enroll_passkey_visited', { applicationId })
  }, [applicationId])

  const onSubmit = async (): Promise<void> => {
    sendEvent('user_enroll_passkey_create_clicked', { applicationId })
    // Sets creating passkey to handle submit button state
    setCreatingPasskey(true)
    try {
      sendEvent('bankmulti_enroll_browser_credential_creation_options_start', { applicationId })

      const registerPasskeyResponse = await registerPasskey()

      if (registerPasskeyResponse.__typename === 'RegisterWebAuthnSuccess') {
        sendEvent('bankmulti_enroll_browser_credential_options_creation_success', { applicationId })

        sendEvent('bankmulti_enroll_browser_credential_creation_start', { applicationId })

        sendEvent('bankmulti_enroll_browser_credential_submit', { applicationId })

        sendEvent('bankmulti_enroll_browser_credential_submit_success', { applicationId })
        // Set the creating of passkey to false as it's no longer happening
        setCreatingPasskey(false)
        // Change screen to passkey persisted
        setPasskeyPersisted(true)
      } else {
        sendEvent('bankmulti_enroll_browser_credential_options_creation_error', { applicationId })
        // TODO: NAVIGATE TO ERROR PAGE
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
      setCreatingPasskey(false)
    } catch (e) {
      log.error('Error submitting form', e)
      setCreatingPasskey(false)
    }
  }

  const onContinue = (): void => {
    sendEvent('user_enroll_passkey_created', { applicationId })
    void navigate(WEALTH_ROUTES.OVERVIEW.path)
  }

  return (
    <EnrollmentMultiLayout title={t('multi.create_passkey.page_title')}>
      <>
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading || isLoadingPasskeys ? (
          <div className="w-full">
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton count={1} height="100px" className="mb-10" />
              <Skeleton count={5} height="50px" />
            </SkeletonTheme>
          </div>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !isErrorPasskeys && !error && applicationId && !passkeyPersisted ? (
          <>
            <h1 className="typo-primary-2xl-light text-text-header mb-md">{t('multi.create_passkey.title')} </h1>
            <span className="typo-primary-lg-semibold text-text-header mb-lg">
              {t('multi.create_passkey.description')}
            </span>

            <IconPasskey className="w-1/3 h-fit" />

            <div className="flex flex-col gap-md mt-lg">
              <span className="typo-primary-base-semibold text-text-header">
                {t('multi.create_passkey.what_are_passkeys')}
              </span>
              <span className="typo-primary-base text-text-meta">
                {t('multi.create_passkey.what_are_passkeys_description')}
              </span>

              <span className="typo-primary-base-semibold text-text-header">
                {t('multi.create_passkey.where_passkeys_saved')}
              </span>
              <span className="typo-primary-base text-text-meta">
                {t('multi.create_passkey.where_passkeys_saved_description')}
              </span>
            </div>

            <div className="bg-sky-100 p-md flex flex-row rounded-lg justify-between mt-lg hidden md:flex">
              <div className="flex flex-row text-[#174a6f] typo-primary-base-semibold w-1/2">
                <div>{t('multi.create_passkey.bluetooth_enabled')}</div>
              </div>
              <Link
                href={config.zendeskArticles.helpCenterFAQs}
                className="flex flex-row gap-sm text-text-action w-1/2 justify-end items-center"
                testId="faq-button"
              >
                {t('multi.create_passkey.how_passkeys_work')}
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            </div>
            <div className="flex flex-grow md:hidden" />

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isLoading={creatingPasskey}
              testId="registerPasskey_button"
              onClick={() => {
                void onSubmit()
              }}
            >
              {t('multi.create_passkey.create_passkey')}
            </Button>
          </>
        ) : null}

        {passkeyPersisted && !isLoadingPasskeys && !isLoading ? (
          <>
            <h1 className="typo-primary-2xl-light text-text-header mb-md">
              {t('multi.create_passkey.passkey_created_title')}
            </h1>

            <SvgPasskeyAdded className="w-1/3 h-fit" strokeColor="rgb(var(--color-green-500))" />

            <div className="flex flex-col gap-md mt-lg">
              <span className="typo-primary-base-semibold text-text-header">
                {t('multi.create_passkey.better_sign_in')}
              </span>
              <span className="typo-primary-base text-text-meta">{t('multi.create_passkey.single_tap')}</span>
              <span className="typo-primary-base-semibold text-text-header">
                {t('multi.create_passkey.manage_passkey')}
              </span>
              <span className="typo-primary-base text-text-meta">{t('multi.create_passkey.crud_description')}</span>
            </div>
            <div className="flex flex-grow md:hidden" />

            <Button
              type="submit"
              className="w-full md:w-min mt-xl flex"
              isLoading={creatingPasskey}
              testId="primaryAction_button"
              onClick={() => {
                onContinue()
              }}
            >
              {t('multi.create_passkey.continue')}
            </Button>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {(error ?? apiError) ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </>
    </EnrollmentMultiLayout>
  )
}
